import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import noLogo from "../../../../assets/img/brand/noLogo";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const safeText = (value, fallback = "—") => {
  const text = (value ?? "").toString().trim();
  return text.length ? text : fallback;
};

const toNum = (value) => {
  const num = Number(value || 0);
  return Number.isFinite(num) ? num : 0;
};

const money = (value) =>
  Math.round((toNum(value) + Number.EPSILON) * 100) / 100;

const fmtMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(money(value));

const formatDate = (value) => {
  const defaultDate = moment().subtract(1, "month").endOf("month");
  const date = moment.isMoment(value)
    ? value
    : value
      ? moment(value, ["DD/MM/YYYY", "YYYY-MM-DD", moment.ISO_8601], true)
      : defaultDate;
  return date.isValid()
    ? date.format("DD/MM/YYYY")
    : defaultDate.format("DD/MM/YYYY");
};

const layoutGrid = {
  hLineWidth: () => 0.8,
  vLineWidth: () => 0.8,
  hLineColor: () => "#000",
  vLineColor: () => "#000",
  paddingLeft: () => 5,
  paddingRight: () => 5,
  paddingTop: () => 3,
  paddingBottom: () => 3,
};

const layoutNoBorder = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
};

const makeLogoBlock = (logoDataUrl) => {
  if (logoDataUrl) {
    return { image: logoDataUrl, width: 140 };
  }

  return {
    layout: layoutNoBorder,
    width: 150,
    table: { widths: ["*"], body: [[{ image: noLogo, width: 140 }]] },
  };
};

const ncfBranchAnnexReport = (data = {}) => {
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const totalClaimValue = rows.reduce(
    (acc, row) => acc + money(row?.claimValue),
    0,
  );
  const totalClaimPayment = rows.reduce(
    (acc, row) => acc + money(row?.claimPayment),
    0,
  );
  const consultoryRncRaw = (data?.consultoryRnc ?? "").toString().trim();
  const insurerRncRaw = (data?.insurerRnc ?? "").toString().trim();

  const consultoryHeaderLines = [
    {
      text: safeText(data?.consultoryName, "Consultorio") + "\n",
      bold: true,
    },
    { text: safeText(data?.consultoryAddress, "Direccion") + "\n" },
  ];

  if (consultoryRncRaw) {
    consultoryHeaderLines.push(`RNC ${consultoryRncRaw}`);
  }

  const voucherDueDate = moment(data?.voucherDueDate);
  const voucherDueDateLabel = voucherDueDate.isValid()
    ? voucherDueDate.format("DD/MM/YYYY")
    : "N/A";
  const generatedDateLabel = formatDate(
    data?.generatedAt || data?.dateLabel || data?.date,
  );

  return {
    pageSize: "LETTER",
    pageMargins: [30, 30, 30, 24],
    defaultStyle: { fontSize: 10 },
    styles: {
      title: { bold: true, fontSize: 13, decoration: "underline" },
      th: { bold: true, fontSize: 10 },
      td: { fontSize: 10 },
    },
    content: [
      {
        columns: [
          {
            width: 170,
            stack: [
              { ...makeLogoBlock(data?.logoDataUrl) },
              {
                text: consultoryHeaderLines,
                margin: [0, 2, 0, 0],
              },
              {
                margin: [0, 12, 0, 0],
                text: [
                  { text: "Aseguradora: ", bold: true },
                  safeText(data?.insurerName),
                ],
              },
              ...(insurerRncRaw
                ? [{ text: [{ text: "RNC: ", bold: true }, insurerRncRaw] }]
                : []),
            ],
          },
          {
            width: "*",
            margin: [0, 36, 0, 0],
            fontSize: 13,
            stack: [
              {
                text: "Relación de Autorizaciones Por Sucursal",
                style: "title",
                alignment: "right",
                bold: true,
                fontSize: 13,
              },
              {
                text: `Fecha: ${generatedDateLabel}`,
                alignment: "right",
                margin: [0, 8, 0, 0],
                bold: true,
                fontSize: 10,
              },
              {
                text: [
                  { text: "NCF: ", bold: true },
                  { text: safeText(data?.ncf, ""), decoration: "underline" },
                ],
                alignment: "right",
                margin: [0, 36, 0, 0],
              },
              {
                text: [
                  { text: "Valido Hasta: ", bold: true },
                  { text: voucherDueDateLabel, decoration: "underline" },
                ],
                alignment: "right",
              },
            ],
          },
        ],
        columnGap: 10,
      },
      {
        text: [{ text: "Periodo: ", bold: true }, safeText(data?.periodLabel)],
        alignment: "right",
      },
      {
        table: {
          headerRows: 1,
          widths: ["*", 120, 120],
          body: [
            [
              { text: "Sucursal", style: "th", alignment: "center" },
              { text: "Monto Reclamado", style: "th", alignment: "center" },
              { text: "Monto Autorizado", style: "th", alignment: "center" },
            ],
            ...(rows.length
              ? rows.map((row) => [
                  { text: safeText(row?.branchName, "Sucursal"), style: "td" },
                  {
                    text: fmtMoney(row?.claimValue),
                    style: "td",
                    alignment: "right",
                  },
                  {
                    text: fmtMoney(row?.claimPayment),
                    style: "td",
                    alignment: "right",
                  },
                ])
              : [
                  [
                    {
                      text: "Sin datos",
                      style: "td",
                      colSpan: 3,
                      alignment: "center",
                    },
                    {},
                    {},
                  ],
                ]),
          ],
        },
        layout: layoutGrid,
      },
      {
        margin: [310, 12, 0, 0],
        table: {
          widths: [110, 110],
          body: [
            [
              { text: "Total Reclamado", style: "th", alignment: "center" },
              {
                text: fmtMoney(totalClaimValue),
                style: "td",
                alignment: "right",
              },
            ],
            [
              { text: "Total Pagado", style: "th", alignment: "center" },
              {
                text: fmtMoney(totalClaimPayment),
                style: "td",
                alignment: "right",
              },
            ],
            [
              { text: "Diferencia", style: "th", alignment: "center" },
              {
                text: fmtMoney(totalClaimValue - totalClaimPayment),
                style: "td",
                alignment: "right",
              },
            ],
          ],
        },
        layout: layoutGrid,
      },
    ],
  };
};

export const openNcfBranchAnnexReport = (data = {}) => {
  pdfMake.createPdf(ncfBranchAnnexReport(data)).open();
};
